// App.tsx
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardActionArea,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  Chip,
  Breadcrumbs,
  Link as MuiLink
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import StorageIcon from '@mui/icons-material/Storage';
import SettingsIcon from '@mui/icons-material/Settings';
import GroupIcon from '@mui/icons-material/Group';
import VideocamIcon from '@mui/icons-material/Videocam';
import CameraIndoorIcon from '@mui/icons-material/CameraIndoor';
import MemoryIcon from '@mui/icons-material/Memory';
import BusinessIcon from '@mui/icons-material/Business';
import DomainVerificationIcon from '@mui/icons-material/DomainVerification';
import DescriptionIcon from '@mui/icons-material/Description';
import SyncIcon from '@mui/icons-material/Sync';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ServerList from '../../components/Serverlist';
import SchoolIcon from '@mui/icons-material/School';
import MessageIcon from '@mui/icons-material/Message';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

// Typdefinitionen für die Kacheln
interface ActionTile {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  path: string;
  category: 'servers' | 'accounts' | 'devices' | 'system';
}

const Backoffice: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [selectedTile, setSelectedTile] = useState<string | null>(null);

  // Definieren aller Aktionskacheln mit Kategorien
  const actionTiles: ActionTile[] = [
    // Server-Kategorie
    {
      id: 'servers',
      title: 'Server',
      subtitle: 'Server verwalten und überwachen',
      icon: <StorageIcon fontSize="large" />,
      path: '/servers',
      category: 'servers'
    },
    
    // Account-Kategorie
    {
      id: 'accounts',
      title: 'Kunden',
      subtitle: 'Kunden und Betreiber verwalten',
      icon: <GroupIcon fontSize="large" />,
      path: '/accounts-admin',
      category: 'accounts'
    },
    {
      id: 'licenses',
      title: 'Lizenzen',
      subtitle: 'Lizenzstatus und -verwaltung',
      icon: <VpnKeyIcon fontSize="large" />,
      path: '/license-admin',
      category: 'accounts'
    },
    {
      id: 'customer-info',
      title: 'Kundenanfragen',
      subtitle: 'Verwaltung von Anfragen & Kontakten',
      icon: <MessageIcon fontSize="large" />,
      path: '/customer-management',
      category: 'accounts'
    },
    
    // Geräte-Kategorie
    {
      id: 'camera-training',
      title: 'Kamera-Trainingsdaten',
      subtitle: 'Training-Status der Kameras',
      icon: <DomainVerificationIcon fontSize="large" />,
      path: '/camera-training',
      category: 'system'
    },
    {
      id: 'raspberry-pis',
      title: 'Raspberry Pi',
      subtitle: 'Pi Geräte konfigurieren',
      icon: <MemoryIcon fontSize="large" />,
      path: '/raspberry-pis',
      category: 'devices'
    },

    
    {
      id: 'training-management',
      title: 'Training Management',
      subtitle: 'KI-Training steuern und verwalten',
      icon: <SchoolIcon fontSize="large" />,
      path: '/training-management',
      category: 'system'
    },
    {
      id: 'video-inspector',
      title: 'Videoinspektor',
      subtitle: 'Video-Inspektion & Analyse',
      icon: <CameraIndoorIcon fontSize="large" />,
      path: '/video-inspector',
      category: 'system'
    },
    // Neue Kachel für den Test-Video-Upload
    {
      id: 'video-upload-test',
      title: 'Test Video Upload',
      subtitle: 'Videos für Tests hochladen',
      icon: <CloudUploadIcon fontSize="large" />,
      path: '/video-upload-test',
      category: 'system'
    }
  ];

  // Filterfunktion basierend auf Suchbegriff und aktiver Kategorie
  const filteredTiles = actionTiles.filter(tile => {
    const matchesSearch = 
      tile.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tile.subtitle.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = activeCategory === 'all' || tile.category === activeCategory;
    
    return matchesSearch && matchesCategory;
  });

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleCategoryChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveCategory(newValue);
  };

  const handleTileClick = (path: string, id: string) => {
    if (id === 'servers') {
      setSelectedTile('servers');
    } else {
      navigate(path);
    }
  };

  const handleBackToTiles = () => {
    if (selectedTile) {
      // Wenn wir in einer Unteransicht sind, gehen wir zurück zur Kachelübersicht
      setSelectedTile(null);
    } else {
      // Wenn wir bereits in der Kachelübersicht sind, navigieren wir zurück zum Dashboard
      navigate('/dashboard');
    }
  };

  const getCategoryLabel = (category: string): string => {
    switch(category) {
      case 'servers': return 'Server';
      case 'accounts': return 'Konten';
      case 'devices': return 'Geräte';
      case 'system': return 'System';
      default: return 'Alle';
    }
  };

  if (selectedTile === 'servers') {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Breadcrumbs Navigation */}
        <Breadcrumbs aria-label="Navigationspfad" sx={{ mb: 2 }}>
          <MuiLink 
            color="inherit" 
            component="button"
            variant="body1"
            onClick={() => navigate('/dashboard')}
            sx={{ textDecoration: 'none' }}
          >
            Dashboard
          </MuiLink>
          <MuiLink 
            color="inherit" 
            component="button"
            variant="body1"
            onClick={() => setSelectedTile(null)}
            sx={{ textDecoration: 'none' }}
          >
            Administration
          </MuiLink>
          <Typography color="text.primary">Server Verwaltung</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton 
            sx={{ mr: 2 }}
            onClick={handleBackToTiles}
            aria-label="Zurück zur Übersicht"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Server Verwaltung
          </Typography>
        </Box>
        <ServerList />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs Navigation */}
      <Breadcrumbs aria-label="Navigationspfad" sx={{ mb: 2 }}>
        <MuiLink 
          color="inherit" 
          component="button"
          variant="body1"
          onClick={() => navigate('/dashboard')}
          sx={{ textDecoration: 'none' }}
        >
          Dashboard
        </MuiLink>
        <Typography color="text.primary">Administration</Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            sx={{ mr: 2 }}
            onClick={handleBackToTiles}
            aria-label="Zurück zum Dashboard"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Administration
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            placeholder="Suchen..."
            size="small"
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ width: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {/* Kategorie-Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeCategory} 
          onChange={handleCategoryChange}
          aria-label="Kategorien"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <DashboardIcon sx={{ mr: 1 }} fontSize="small" />
                <span>Alle</span>
              </Box>
            } 
            value="all" 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StorageIcon sx={{ mr: 1 }} fontSize="small" />
                <span>Server</span>
              </Box>
            } 
            value="servers" 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <GroupIcon sx={{ mr: 1 }} fontSize="small" />
                <span>Konten</span>
              </Box>
            } 
            value="accounts" 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MemoryIcon sx={{ mr: 1 }} fontSize="small" />
                <span>Geräte</span>
              </Box>
            } 
            value="devices" 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SettingsIcon sx={{ mr: 1 }} fontSize="small" />
                <span>System</span>
              </Box>
            } 
            value="system" 
          />
        </Tabs>
      </Box>

      {/* Kacheln */}
      <Grid container rowSpacing={6} columnSpacing={3}>
        {filteredTiles.map((tile) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={tile.id}>
            <Card 
              sx={{ 
                height: '100%',
                minHeight: '120px',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
                }
              }}
            >
              <CardActionArea 
                sx={{ height: '100%' }}
                onClick={() => handleTileClick(tile.path, tile.id)}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Box sx={{ 
                      bgcolor: 'primary.light', 
                      color: 'primary.contrastText',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      p: 0.75,
                      width: 32,
                      height: 32
                    }}>
                      {tile.icon}
                    </Box>
                    <Chip 
                      label={getCategoryLabel(tile.category)} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ fontSize: '0.65rem', height: 20 }}
                    />
                  </Box>
                  <Typography variant="h6" component="h2" sx={{ fontSize: '0.95rem', mt: 0.5, mb: 0.5 }}>
                    {tile.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                    {tile.subtitle}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
        
        {filteredTiles.length === 0 && (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary">
                Keine Ergebnisse gefunden
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bitte versuchen Sie einen anderen Suchbegriff oder eine andere Kategorie.
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default Backoffice;
